import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Users, X, Check, Send, Contact as ContactIcon, Search, ArrowRight } from "lucide-react";

const InviteDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const res = await api.post("/admin/invites", form);
      setResult(res.data);
      onCreated?.();
      toast.success("Client added — invite link ready to copy");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const url = result ? `${window.location.origin}${result.invite_url_path}` : "";

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1A1A]/50 grid place-items-center p-4" onClick={onClose}>
      <div
        className="byrd-card w-full max-w-lg p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-dialog"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// New Client</div>
            <h2 className="font-serif text-2xl font-bold mt-1">Add Client</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md border border-[#E4DFD1]" data-testid="invite-close">
            <X size={16} />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Full name *</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)}
                  data-testid="invite-name"
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  data-testid="invite-email"
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Company</label>
                <input value={form.company} onChange={(e) => set("company", e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Phone</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                />
              </div>
            </div>
            <div className="text-xs text-[#6B6558]">
              This adds the client to your roster and generates a portal invite link.
              A default document checklist (personal + business 3-yr tax returns, resume, entity docs, etc.)
              is attached automatically — you can edit it after. <b>Loan details</b> live on scenarios, so one
              client can have multiple deals in flight. Send the invite link now or later from the client&apos;s page.
            </div>
            <button type="submit" disabled={busy} className="byrd-btn byrd-btn-dark w-full" data-testid="invite-submit">
              {busy ? "Adding…" : "Add Client"}
            </button>
          </form>
        ) : (
          <div className="mt-6 space-y-4" data-testid="invite-result">
            <div className="byrd-chip byrd-chip-green"><Check size={12} /> Client added</div>
            <p className="text-sm text-[#2A2A2A]">
              <span className="font-semibold">{result.user.name}</span> is now in your roster.
              No email has been sent yet — set up their scenarios &amp; fee agreement first, then
              trigger the portal invite when you&apos;re ready. You can copy the link below or
              send the invite email now.
            </p>
            <div className="flex items-center gap-2">
              <input readOnly value={url} className="flex-1 h-11 px-3 rounded-md border border-[#E4DFD1] bg-[#FBF8F1] font-mono text-xs"
                data-testid="invite-link" />
              <button onClick={copy} className="byrd-btn byrd-btn-outline h-11 px-3" data-testid="invite-copy">
                <Copy size={14} /> Copy
              </button>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.post(`/admin/users/${result.user.id}/send-invite`);
                  toast.success(`Portal invite emailed to ${result.user.email}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to send invite");
                }
              }}
              className="byrd-btn byrd-btn-dark w-full h-11"
              data-testid="invite-send-email"
            >
              <Send size={14} /> Send Portal Invite Email
            </button>
            <div className="flex gap-3 pt-2">
              <Link to={`/admin/clients/${result.user.id}`} className="byrd-btn byrd-btn-primary flex-1" data-testid="invite-open-client">
                Open client
              </Link>
              <button onClick={onClose} className="byrd-btn byrd-btn-outline flex-1">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Sort key persists across visits — this pref is broker-specific, not
  // deal-specific, so localStorage is the right home.
  const [sortKey, setSortKey] = useState(
    () => localStorage.getItem("byrd.clients.sort") || "added_desc",
  );
  useEffect(() => { localStorage.setItem("byrd.clients.sort", sortKey); }, [sortKey]);

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Sort a copy — never mutate `clients` state directly. The backend already
  // returns them in insertion order (`added_desc`), so that path is a no-op.
  const sortedClients = useMemo(() => {
    const arr = [...clients];
    const firstName = (c) => ((c.name || "").trim().split(/\s+/)[0] || "").toLowerCase();
    const lastName = (c) => {
      const parts = (c.name || "").trim().split(/\s+/);
      return (parts[parts.length - 1] || "").toLowerCase();
    };
    switch (sortKey) {
      case "first_asc":
        arr.sort((a, b) => firstName(a).localeCompare(firstName(b)));
        break;
      case "first_desc":
        arr.sort((a, b) => firstName(b).localeCompare(firstName(a)));
        break;
      case "last_asc":
        arr.sort((a, b) => lastName(a).localeCompare(lastName(b)));
        break;
      case "added_asc":
        // Backend default is newest-first; reverse for oldest-first.
        arr.reverse();
        break;
      case "added_desc":
      default:
        // Leave as returned by API (newest first)
        break;
    }
    return arr;
  }, [clients, sortKey]);

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase text-[#6B6558] tracking-widest">// Clients</div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2">Client Roster.</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {clients.length > 1 && (
            <label className="flex items-center gap-2 text-xs text-[#6B6558]">
              <span className="font-mono text-[10px] uppercase tracking-widest">Sort</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                data-testid="clients-sort-select"
                className="h-9 px-2 rounded-md border border-[#E4DFD1] bg-white text-sm"
              >
                <option value="added_desc">Newest added</option>
                <option value="added_asc">Oldest added</option>
                <option value="first_asc">First name (A–Z)</option>
                <option value="first_desc">First name (Z–A)</option>
                <option value="last_asc">Last name (A–Z)</option>
              </select>
            </label>
          )}
          <button onClick={() => setPickerOpen(true)} className="byrd-btn byrd-btn-outline" data-testid="from-contact-btn">
            <ContactIcon size={14} /> New Client from Contact…
          </button>
          <button onClick={() => setOpen(true)} className="byrd-btn byrd-btn-dark" data-testid="new-invite-btn">
            <Plus size={14} /> Add Client
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B6558]">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="byrd-card p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#F3EEE0] grid place-items-center text-[#C89434]">
            <Users size={22} />
          </div>
          <h3 className="font-serif text-2xl font-bold mt-4">No clients yet.</h3>
          <p className="text-[#6B6558] mt-2 max-w-md mx-auto">
            Add your first client. You&apos;ll get a shareable portal invite link you can send now or later.
          </p>
          <button onClick={() => setOpen(true)} className="byrd-btn byrd-btn-primary mt-5">
            Add First Client <Plus size={14} />
          </button>
        </div>
      ) : (
        <div className="byrd-card overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_.9fr_.9fr_.9fr_.8fr] border-b border-[#E4DFD1] bg-[#FBF8F1]">
            {["Client", "Email", "Scenarios", "Pending", "Uploaded", "Reviewed"].map((h) => (
              <div key={h} className="px-4 py-3 text-[11px] uppercase font-mono tracking-widest text-[#6B6558]">{h}</div>
            ))}
          </div>
          {sortedClients.map((c) => (
            <Link
              key={c.id}
              to={`/admin/clients/${c.id}`}
              data-testid={`client-row-${c.id}`}
              className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_.9fr_.9fr_.9fr_.8fr] border-b border-[#E4DFD1] last:border-b-0 hover:bg-[#FBF8F1] transition-colors"
            >
              <div className="px-4 py-4">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-[#6B6558]">{c.company || "—"}</div>
              </div>
              <div className="px-4 py-4 text-sm text-[#2A2A2A] truncate">{c.email}</div>
              <div className="px-4 py-4 text-sm">
                {c.scenario_count > 0 ? (
                  <div>
                    <div className="font-mono text-sm">{c.scenario_count}</div>
                    {c.latest_scenario?.loan_type && (
                      <div className="text-[11px] text-[#6B6558] truncate">Latest: {c.latest_scenario.loan_type}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-[#6B6558]">—</span>
                )}
              </div>
              <div className="px-4 py-4 font-mono text-sm">{c.doc_summary.pending}</div>
              <div className="px-4 py-4 font-mono text-sm">{c.doc_summary.uploaded}</div>
              <div className="px-4 py-4 font-mono text-sm">
                <span className="byrd-chip byrd-chip-green">{c.doc_summary.reviewed}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <InviteDialog open={open} onClose={() => setOpen(false)} onCreated={load} />
      <FromContactPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onCreated={load} />
    </div>
  );
}

const FromContactPicker = ({ open, onClose, onCreated }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResult(null); return; }
    setLoading(true);
    api.get("/admin/contacts")
      .then((r) => setContacts(r.data || []))
      .finally(() => setLoading(false));
  }, [open]);

  const eligible = useMemo(
    () => contacts.filter((c) => c.email && !c.client_user_id),
    [contacts],
  );
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return eligible;
    return eligible.filter((c) =>
      [c.name, c.email, c.phone, (c.tags || []).join(" ")].join(" ").toLowerCase().includes(term)
    );
  }, [eligible, q]);

  if (!open) return null;

  const promote = async (c) => {
    setBusyId(c.id);
    try {
      const r = await api.post(`/admin/contacts/${c.id}/promote-to-client`);
      setResult({ contact: c, ...r.data });
      onCreated?.();
      toast.success(`${c.name} is now a client — set up their scenarios next.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't convert contact");
    } finally {
      setBusyId(null);
    }
  };

  const skippedNoEmail = contacts.filter((c) => !c.email).length;
  const alreadyClients = contacts.filter((c) => c.client_user_id).length;

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1A1A]/50 grid place-items-center p-4" onClick={onClose}>
      <div className="byrd-card w-full max-w-2xl p-6 md:p-8 max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()} data-testid="from-contact-dialog">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// New Client · From Contact</div>
            <h2 className="font-serif text-2xl font-bold mt-1">Pick a contact.</h2>
            <p className="text-xs text-[#6B6558] mt-2 max-w-md">
              Convert someone in your rolodex into a client. We&apos;ll create their portal user with
              the contact&apos;s info — no email goes out yet, so you can build the doc list first.
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md border border-[#E4DFD1]"
                  data-testid="from-contact-close"><X size={16} /></button>
        </div>

        {!result ? (
          <>
            <div className="relative mt-5">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6558]" />
              <input
                autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, email, phone, tag…"
                className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E4DFD1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                data-testid="from-contact-search"
              />
            </div>
            <div className="text-[11px] text-[#6B6558] mt-2 font-mono">
              {loading ? "Loading contacts…" : `${filtered.length} eligible`}
              {skippedNoEmail > 0 && ` · ${skippedNoEmail} missing email`}
              {alreadyClients > 0 && ` · ${alreadyClients} already clients`}
            </div>

            <div className="mt-3 flex-1 overflow-y-auto border border-[#E4DFD1] rounded-md divide-y divide-[#E4DFD1]"
                 data-testid="from-contact-list">
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-center text-sm text-[#6B6558]">
                  {q ? "No contacts match." : (eligible.length === 0 ? "No eligible contacts yet — add one on the Contacts page first." : "Try a different search.")}
                </div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => promote(c)}
                  disabled={busyId === c.id}
                  className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-[#FBF8F1] disabled:opacity-60 transition-colors"
                  data-testid={`promote-contact-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#F3EEE0] grid place-items-center text-[#7A5410] font-serif text-sm shrink-0">
                    {(c.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{c.name}</div>
                    <div className="text-[11px] text-[#6B6558] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                  </div>
                  <ArrowRight size={14} className={busyId === c.id ? "text-[#C89434] animate-pulse" : "text-[#6B6558]"} />
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="mt-5 space-y-4" data-testid="from-contact-result">
            <div className="byrd-chip byrd-chip-green"><Check size={12} /> {result.contact.name} is now a client</div>
            <p className="text-sm text-[#2A2A2A]">
              No invite email has been sent. Head to the client&apos;s page to build their scenario and
              doc list, then send the portal invite when you&apos;re ready.
            </p>
            <div className="flex gap-3 pt-1">
              <Link
                to={`/admin/clients/${result.user.id}`}
                onClick={onClose}
                className="byrd-btn byrd-btn-primary flex-1"
                data-testid="from-contact-open-client"
              >
                Open client & build scenario <ArrowRight size={13} />
              </Link>
              <button onClick={() => { setResult(null); setQ(""); }}
                      className="byrd-btn byrd-btn-outline flex-1"
                      data-testid="from-contact-convert-another">
                Convert another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
